import { INDICATEURS_AFFICHES, indicateur } from '@/lib/controle';
import type { ChangementTraduction, ControleNoyau, IndicateurControle } from '@rosetta/shared';
import { CLASSES_CARTE, idCarteIndicateur } from './carte-indicateur';
import { MarqueAjout } from './marque-ajout';
import { MarqueChangement } from './marque-changement';
import { PuceCode } from './puce-code';

/** `id` of the card of the changes the translation declares: it has no indicator of Tahiti. */
export const ID_CARTE_CHANGEMENTS = 'changements-declares';

/**
 * The card of the additions, when there is something to show in it: the indicator 2.3 of the
 * control AND at least one addition (an indicator without an element has nothing to list).
 */
export function indicateurAjoutsAffiche(controle: ControleNoyau): IndicateurControle | undefined {
  const trouve = indicateur(controle, INDICATEURS_AFFICHES.ajouts);
  return (controle.ajouts ?? []).length === 0 ? undefined : trouve;
}

interface CarteAjoutsProps {
  controle: ControleNoyau;
  /** The indicator 2.3 (see `indicateurAjoutsAffiche`): its number is the id of the card, its name the title. */
  indicateur: IndicateurControle;
}

/**
 * The card of the additions of the control (indicator 2.3), in the column of the cards of "Texte
 * traduit" (#30, theme "Contenu", after the faithfulness of the affirmations as in the numbering
 * of Tahiti): the same information as the marks of "Avant / après", listed. Each passage is the
 * same mark with the same bubble as in the text (`MarqueAjout`: tint, name, hover and focus), and
 * what Tahiti says of it (its type, its `detail`) is written beside it, so it is read without
 * hovering. It is the target of the counters (its id is the one of the indicator, its title the
 * name Tahiti gives it); it says no status and no summary (decision of 2026-10-13): only "ajout".
 */
export function CarteAjouts({ controle, indicateur: trouve }: CarteAjoutsProps) {
  const id = idCarteIndicateur(trouve.numero);
  return (
    <article id={id} tabIndex={-1} aria-labelledby={`${id}-titre`} className={CLASSES_CARTE}>
      <h4 id={`${id}-titre`} className="font-bold text-body leading-[1.35]">
        {trouve.nom}
      </h4>
      <ul className="mt-3 flex flex-col gap-3">
        {(controle.ajouts ?? []).map((ajout) => (
          <li key={`${ajout.type.code}|${ajout.passage ?? ''}|${ajout.detail ?? ''}`}>
            <div className="flex flex-wrap items-center gap-2 text-small leading-[1.7]">
              <PuceCode>{ajout.type.libelle}</PuceCode>
              {ajout.passage === undefined ? null : (
                <MarqueAjout ajout={ajout}>{ajout.passage}</MarqueAjout>
              )}
            </div>
            {ajout.detail === undefined ? null : (
              <p className="mt-1 text-gray-700 text-small leading-normal">{ajout.detail}</p>
            )}
          </li>
        ))}
      </ul>
    </article>
  );
}

interface CarteChangementsProps {
  changements: readonly ChangementTraduction[];
}

/**
 * The card of the changes the translation declares, in the same column, under the one of the
 * additions (#30): each passage in its mark (`MarqueChangement`, same bubble as in the text), its
 * type and its `description` written beside it. It has no indicator of Tahiti and no status: it
 * is no target of a counter.
 */
export function CarteChangements({ changements }: CarteChangementsProps) {
  return (
    <article
      id={ID_CARTE_CHANGEMENTS}
      aria-labelledby={`${ID_CARTE_CHANGEMENTS}-titre`}
      className={CLASSES_CARTE}
    >
      <h4 id={`${ID_CARTE_CHANGEMENTS}-titre`} className="font-bold text-body leading-[1.35]">
        Changements
      </h4>
      <ul className="mt-3 flex flex-col gap-3">
        {changements.map((changement) => (
          <li key={`${changement.type.code}|${changement.passage}`}>
            <div className="flex flex-wrap items-center gap-2 text-small leading-[1.7]">
              <PuceCode>{changement.type.libelle}</PuceCode>
              <MarqueChangement changement={changement}>{changement.passage}</MarqueChangement>
            </div>
            {changement.description === undefined ? null : (
              <p className="mt-1 text-gray-700 text-small leading-normal">
                {changement.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </article>
  );
}
